import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search, RefreshCw, Download, DollarSign } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import { companyService } from '@/services/companyService';
import { decodeHtmlEntities } from '@/lib/utils';
import { calculatePriceAnalysis, type ProfitStatus } from '@/utils/priceAnalysis';
import { triggerReloadSearch, pickReloadSearch } from '@/utils/reloadSearch';
import * as XLSX from 'xlsx';
import { ChevronUp, ChevronDown } from 'lucide-react';

const ANALYTICS_CATEGORY = 'analytics';

type ProfitStatusFilter = 'all' | ProfitStatus;

interface ProductRow {
  id: number;
  sku_id: string;
  company_id: number;
  company_sku_key: string | null;
  sku_name: string | null;
  sku_name_chi: string | null;
  brand: string | null;
  barcode: string | null;
  sku_status: string | null;
  original_price: number;
  selling_price: number | null;
  category_code: string | null;
}

interface PriceAnalysisRow extends ProductRow {
  averageCost: number;
  commissionRate: number;
  effectivePrice: number;
  commissionFee: number;
  realCost: number;
  finalProfit: number;
  profitStatus: ProfitStatus;
}

const toNullable = (v?: string) => (v?.trim() ? v.trim() : undefined);

const PriceAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language, t } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [skuId, setSkuId] = useState('');
  const [ean, setEan] = useState('');
  const [skuName, setSkuName] = useState('');
  const [productId, setProductId] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [onlineStatusFilter, setOnlineStatusFilter] = useState<'all' | 'ONLINE' | 'OFFLINE'>('ONLINE');
  const [storewideDiscount, setStorewideDiscount] = useState(0);
  const [profitStatusFilter, setProfitStatusFilter] = useState<ProfitStatusFilter>('all');

  const [warehouseOptions, setWarehouseOptions] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<PriceAnalysisRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortConfig, setSortConfig] = useState<{
    key: 'finalProfit' | null;
    direction: 'asc' | 'desc' | null;
  }>({
    key: null,
    direction: null,
  });

  const maxDiscount = 100;

  const total = rows.length;
  
  // 排序和分頁
  const sortedAndPaginatedRows = useMemo(() => {
    let sorted = [...rows];
    
    // 如果用戶點擊了利潤排序，應用該排序
    if (sortConfig.key === 'finalProfit' && sortConfig.direction) {
      sorted.sort((a, b) => {
        if (sortConfig.direction === 'asc') {
          return a.finalProfit - b.finalProfit;
        } else {
          return b.finalProfit - a.finalProfit;
        }
      });
    } else {
      // 默認排序：先按品牌，再按 SKU ID
      sorted.sort((a, b) => {
        const brandA = (a.brand || '').toLowerCase();
        const brandB = (b.brand || '').toLowerCase();
        if (brandA !== brandB) {
          return brandA.localeCompare(brandB);
        }
        return (a.sku_id || '').localeCompare(b.sku_id || '');
      });
    }
    
    // 分頁
    const from = (page - 1) * pageSize;
    return sorted.slice(from, from + pageSize);
  }, [rows, page, pageSize, sortConfig]);
  
  const paginatedRows = sortedAndPaginatedRows;

  const loadFilterOptions = useCallback(async (companyIdNum: number) => {
    try {
      const { data: whData } = await (supabase
        .from('products')
        .select('warehouse') as any)
        .eq('company_id', companyIdNum)
        .not('warehouse', 'is', null)
        .order('warehouse');
      const { data: brData } = await (supabase
        .from('products')
        .select('brand') as any)
        .eq('company_id', companyIdNum)
        .not('brand', 'is', null)
        .order('brand');
      setWarehouseOptions(
        Array.from(new Set((whData as any[] || []).map((r: any) => String(r.warehouse)))).filter(Boolean) as string[]
      );
      setBrandOptions(
        Array.from(new Set((brData as any[] || []).map((r: any) => String(r.brand)))).filter(Boolean) as string[]
      );
    } catch {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    const discount = searchParams.get('discount');
    const profitStatus = searchParams.get('profitStatus');
    const q = searchParams.get('q');
    const sku = searchParams.get('skuId');
    const eanParam = searchParams.get('ean');
    const name = searchParams.get('skuName');
    const pid = searchParams.get('productId');
    const wh = searchParams.get('warehouse');
    const brand = searchParams.get('brand');
    const status = searchParams.get('status');
    const p = searchParams.get('page');
    const ps = searchParams.get('pageSize');
    if (discount !== null) setStorewideDiscount(Math.max(0, Math.min(maxDiscount, parseInt(discount, 10) || 0)));
    if (profitStatus !== null && ['all', 'normal', 'low', 'negative'].includes(profitStatus))
      setProfitStatusFilter(profitStatus as ProfitStatusFilter);
    if (q !== null) setSearchTerm(q);
    if (sku !== null) setSkuId(sku);
    if (eanParam !== null) setEan(eanParam);
    if (name !== null) setSkuName(name);
    if (pid !== null) setProductId(pid);
    if (wh !== null) setWarehouseFilter(wh);
    if (brand !== null) setBrandFilter(brand);
    if (status !== null && ['all', 'ONLINE', 'OFFLINE'].includes(status))
      setOnlineStatusFilter(status as 'all' | 'ONLINE' | 'OFFLINE');
    if (p !== null) setPage(Math.max(1, parseInt(p, 10) || 1));
    if (ps !== null) setPageSize(Math.max(10, Math.min(100, parseInt(ps, 10) || 20)));
  }, []);

  const updateUrl = useCallback(
    (updates: Record<string, string | number>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === '' || v === 'all' || (k === 'discount' && Number(v) === 0)) next.delete(k);
          else next.set(k, String(v));
        });
        return next;
      });
    },
    [setSearchParams]
  );

  interface FetchDataOverrides {
    storewideDiscount?: number;
    profitStatusFilter?: ProfitStatusFilter;
    searchParams?: {
      searchTerm?: string;
      skuId?: string;
      ean?: string;
      skuName?: string;
      productId?: string;
      warehouseFilter?: string;
      brandFilter?: string;
      onlineStatusFilter?: 'all' | 'ONLINE' | 'OFFLINE';
    };
  }

  const fetchData = useCallback(
    async (overrides?: FetchDataOverrides) => {
      setLoading(true);
      setErrorMessage(null);
      const discountVal = overrides?.storewideDiscount ?? storewideDiscount;
      const profitStatusVal = overrides?.profitStatusFilter ?? profitStatusFilter;
      
      // 使用傳入的搜尋參數或當前 state
      const params = overrides?.searchParams || {
        searchTerm,
        skuId,
        ean,
        skuName,
        productId,
        warehouseFilter,
        brandFilter,
        onlineStatusFilter,
      };
      
      try {
        await ensureAuthReady();
        const companyIdNum =
          currentCompanyId ?? Number(localStorage.getItem('selectedCompanyId') || '0');
        if (!companyIdNum || !Number.isFinite(companyIdNum)) {
          throw new Error(language === 'zh' ? '尚未選擇公司，請先切換公司' : 'No company selected. Please choose a company.');
        }

        const q = toNullable(params.searchTerm);
        let query: any = (supabase
          .from('products')
          .select('id, sku_id, company_id, company_sku_key, sku_name, sku_name_chi, brand, barcode, sku_status, original_price, selling_price, category_code') as any)
          .eq('company_id', companyIdNum)
          .order('created_at', { ascending: false })
          .limit(2000);

      if (q) {
        query = query.or(
          `sku_id.ilike.%${q}%,sku_name.ilike.%${q}%,brand.ilike.%${q}%,product_id.ilike.%${q}%,barcode.ilike.%${q}%`
        );
      }
      if (toNullable(params.skuId)) query = query.ilike('sku_id', `%${toNullable(params.skuId)}%`);
      if (toNullable(params.ean)) query = query.ilike('barcode', `%${toNullable(params.ean)}%`);
      if (toNullable(params.skuName))
        query = query.or(
          `sku_name.ilike.%${toNullable(params.skuName)}%,sku_name_chi.ilike.%${toNullable(params.skuName)}%,sku_name_sc.ilike.%${toNullable(params.skuName)}%`
        );
      if (toNullable(params.productId)) {
        const d = toNullable(params.productId);
        query = query.or(
          `sku_short_description.ilike.%${d}%,sku_short_description_chi.ilike.%${d}%,sku_short_description_sc.ilike.%${d}%,sku_long_description.ilike.%${d}%,sku_long_description_chi.ilike.%${d}%,sku_long_description_sc.ilike.%${d}%`
        );
      }
      if (toNullable(params.warehouseFilter)) query = query.eq('warehouse', toNullable(params.warehouseFilter));
      if (toNullable(params.brandFilter)) query = query.eq('brand', toNullable(params.brandFilter));
      if (params.onlineStatusFilter !== 'all') query = query.eq('sku_status', params.onlineStatusFilter);

      const { data: products, error } = await query;
      if (error) throw error;

      const productList = (products || []) as unknown as ProductRow[];
      if (productList.length === 0) {
        setRows([]);
        return;
      }

      const companySkuKeys = productList
        .map((p) => p.company_sku_key)
        .filter((k): k is string => !!k);
      const categoryCodes = productList
        .map((p) => p.category_code)
        .filter((c): c is string => !!c);

      const costMap = new Map<string, number>();
      if (companySkuKeys.length > 0) {
        const { data: costs } = await supabase
          .from('product_costs')
          .select('company_sku_key, average_cost')
          .eq('company_id', companyIdNum)
          .in('company_sku_key', companySkuKeys);
        (costs || []).forEach((c: any) => {
          if (c.company_sku_key != null && c.average_cost != null)
            costMap.set(c.company_sku_key, Number(c.average_cost));
        });
      }

      const commissionMap = new Map<string, number>();
      if (categoryCodes.length > 0) {
        const uniqueCodes = Array.from(new Set(categoryCodes));
        const { data: categories } = await supabase
          .from('categories')
          .select('category_code, standard_delivery_commission')
          .in('category_code', uniqueCodes);
        (categories || []).forEach((c: any) => {
          if (c.category_code != null && c.standard_delivery_commission != null) {
            // standard_delivery_commission is stored as percentage (e.g. 18 = 18%), convert to decimal (0.18)
            const commissionRate = Number(c.standard_delivery_commission) / 100;
            commissionMap.set(c.category_code, commissionRate);
          }
        });
      }

      const discountPct = Math.max(0, Math.min(maxDiscount, discountVal));
      const computed: PriceAnalysisRow[] = productList.map((p) => {
        const basePrice =
          p.selling_price != null && p.selling_price > 0 ? p.selling_price : (p.original_price ?? 0);
        const averageCost = p.company_sku_key ? costMap.get(p.company_sku_key) ?? 0 : 0;
        const commissionRate = p.category_code ? commissionMap.get(p.category_code) ?? 0 : 0;
        const result = calculatePriceAnalysis({
          basePrice,
          averageCost,
          commissionRate,
          inputDiscount: discountPct,
        });
        return {
          ...p,
          averageCost,
          commissionRate,
          effectivePrice: result.effectivePrice,
          commissionFee: result.commissionFee,
          realCost: result.realCost,
          finalProfit: result.finalProfit,
          profitStatus: result.profitStatus,
        };
      });

      const filtered =
        profitStatusVal === 'all'
          ? computed
          : computed.filter((r) => r.profitStatus === profitStatusVal);
      setRows(filtered);
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (msg.includes('尚未選擇公司') || msg.includes('No company selected')) {
        setRows([]);
        setErrorMessage(msg);
      } else {
        setErrorMessage(
          (language === 'zh' ? '獲取資料失敗: ' : 'Failed to fetch data: ') + msg
        );
        toast.error(language === 'zh' ? '載入失敗' : 'Load failed');
      }
    } finally {
      setLoading(false);
    }
    },
  [
    currentCompanyId,
    language,
    searchTerm,
    skuId,
    ean,
    skuName,
    productId,
    warehouseFilter,
    brandFilter,
    onlineStatusFilter,
    storewideDiscount,
    profitStatusFilter,
  ]);

  // 首次載入時回放搜尋（恢復UI並自動執行搜索）
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    (async () => {
      console.log('🔄 售價分析：檢測到待回放的搜索，開始執行');
      try {
        // 確保 Auth/Session ready 再搜
        await supabase.auth.getSession();
        
        // 回填 UI
        const filters = pending.filters;
        if (filters.searchTerm !== undefined) setSearchTerm(filters.searchTerm);
        if (filters.skuId !== undefined) setSkuId(filters.skuId);
        if (filters.ean !== undefined) setEan(filters.ean);
        if (filters.skuName !== undefined) setSkuName(filters.skuName);
        if (filters.productId !== undefined) setProductId(filters.productId);
        if (filters.warehouseFilter !== undefined) setWarehouseFilter(filters.warehouseFilter);
        if (filters.brandFilter !== undefined) setBrandFilter(filters.brandFilter);
        if (filters.onlineStatusFilter !== undefined) setOnlineStatusFilter(filters.onlineStatusFilter);
        if (filters.storewideDiscount !== undefined) setStorewideDiscount(filters.storewideDiscount);
        if (filters.profitStatusFilter !== undefined) setProfitStatusFilter(filters.profitStatusFilter);
        if (filters.page !== undefined) setPage(filters.page);
        if (filters.pageSize !== undefined) setPageSize(filters.pageSize);
        
        // 先加载公司信息和筛选选项
        const c = await companyService.getCurrentCompany();
        setCurrentCompanyId(c ? Number(c.id) : null);
        if (c?.id) {
          await loadFilterOptions(Number(c.id));
        }
        
        // 延迟一点时间让UI状态更新，然后执行搜索
        setTimeout(async () => {
          // 直接传递搜索参数，不依赖state
          await fetchData({
            storewideDiscount: filters.storewideDiscount,
            profitStatusFilter: filters.profitStatusFilter,
            searchParams: {
              searchTerm: filters.searchTerm,
              skuId: filters.skuId,
              ean: filters.ean,
              skuName: filters.skuName,
              productId: filters.productId,
              warehouseFilter: filters.warehouseFilter,
              brandFilter: filters.brandFilter,
              onlineStatusFilter: filters.onlineStatusFilter,
            },
          });
        }, 500);
        
      } catch (error) {
        console.error('售價分析回放搜索失败:', error);
        toast.error(language === 'zh' ? '搜索恢复失败，请重新搜索' : 'Search restore failed, please search again');
      }
    })();
  }, []); // 只在组件挂载时执行一次

  useEffect(() => {
    (async () => {
      const c = await companyService.getCurrentCompany();
      setCurrentCompanyId(c ? Number(c.id) : null);
      if (c?.id) await loadFilterOptions(Number(c.id));
    })();
  }, [loadFilterOptions]);


  const handleSearch = () => {
    // 收集当前的搜索条件
    const filters = {
      searchTerm,
      skuId,
      ean,
      skuName,
      productId,
      warehouseFilter,
      brandFilter,
      onlineStatusFilter,
      storewideDiscount,
      profitStatusFilter,
      page: 1, // 搜索时重置为第一页
      pageSize,
    };
    
    console.log('🔄 售價分析：觸發專業刷新搜索，條件:', filters);
    triggerReloadSearch(filters);
  };

  const handleReset = () => {
    setSearchTerm('');
    setSkuId('');
    setEan('');
    setSkuName('');
    setProductId('');
    setWarehouseFilter('');
    setBrandFilter('');
    setOnlineStatusFilter('ONLINE');
    setStorewideDiscount(0);
    setProfitStatusFilter('all');
    setPage(1);
    setSearchParams({});
    setRows([]);
  };

  const getStatusText = (status: string | null) => {
    const normalized = (status || '').toUpperCase();
    const isOnline = normalized === 'ONLINE' || normalized === 'ACTIVE';
    const isOffline = normalized === 'OFFLINE' || normalized === 'INACTIVE';
    if (language === 'zh') {
      if (isOnline) return '在線';
      if (isOffline) return '離線';
    } else {
      if (isOnline) return 'Online';
      if (isOffline) return 'Offline';
    }
    return status || '-';
  };

  const profitCellClass = (profit: number) => {
    if (profit < 0) return 'text-red-500 font-medium';
    if (profit <= 10) return 'text-yellow-500 font-medium';
    return 'text-green-500 font-medium';
  };

  const handleSortProfit = () => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === 'finalProfit') {
        // 同一個欄位，切換排序方向
        if (prevConfig.direction === 'asc') {
          return { key: 'finalProfit', direction: 'desc' };
        } else if (prevConfig.direction === 'desc') {
          return { key: null, direction: null }; // 取消排序，回到默認排序
        } else {
          return { key: 'finalProfit', direction: 'asc' };
        }
      } else {
        // 開始升序排序
        return { key: 'finalProfit', direction: 'asc' };
      }
    });
  };

  const exportToExcel = () => {
    if (rows.length === 0) {
      toast.error(language === 'zh' ? '沒有資料可匯出' : 'No data to export');
      return;
    }
    const isZh = language === 'zh';
    const headers = isZh
      ? ['SKU ID', '條碼', '品牌', '產品名稱', '狀態', '原價', '特價', '折扣售價', '佣金', '利潤']
      : ['SKU ID', 'Barcode', 'Brand', 'Product Name', 'Status', 'Original Price', 'Selling Price', 'Discounted Price', 'Commission', 'Profit'];
    const data = rows.map((r) => [
      r.sku_id,
      r.barcode ?? '',
      r.brand ?? '',
      decodeHtmlEntities(isZh ? (r.sku_name_chi || r.sku_name) || '' : (r.sku_name || r.sku_name_chi) || ''),
      getStatusText(r.sku_status),
      r.original_price ?? 0,
      r.selling_price ?? r.original_price ?? 0,
      Number(r.effectivePrice.toFixed(2)),
      Number(r.commissionFee.toFixed(2)),
      Number(r.finalProfit.toFixed(2)),
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isZh ? '售價分析' : 'Price Analysis');
    const filename = `price_analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(language === 'zh' ? '已匯出 Excel' : 'Exported to Excel');
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ANALYTICS_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {t('priceAnalysis')}
            </h1>
          </div>
        </div>

        <Card className="bg-card/80 border-border mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '產品 ID' : 'SKU ID'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品 ID' : 'Search SKU ID'}
                  value={skuId}
                  onChange={(e) => setSkuId(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '條碼' : 'Barcode'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 條碼' : 'Search Barcode'}
                  value={ean}
                  onChange={(e) => setEan(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '產品名稱' : 'Product Name'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品名稱' : 'Search Product Name'}
                  value={skuName}
                  onChange={(e) => setSkuName(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '關鍵字' : 'Keyword'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '綜合搜尋' : 'Search'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '全店折扣 (% Off)' : 'Storewide Discount (% Off)'}
                </label>
                <Input
                  type="number"
                  min={0}
                  max={maxDiscount}
                  value={storewideDiscount === 0 ? '' : storewideDiscount}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') setStorewideDiscount(0);
                    else {
                      const n = parseInt(v, 10);
                      if (!Number.isNaN(n)) setStorewideDiscount(Math.max(0, Math.min(maxDiscount, n)));
                    }
                  }}
                  placeholder="0"
                  className="bg-background/50 border-input text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '利潤狀態' : 'Profit Status'}
                </label>
                <Select
                  value={profitStatusFilter}
                  onValueChange={(v) => setProfitStatusFilter(v as ProfitStatusFilter)}
                >
                  <SelectTrigger className="h-10 bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部' : 'All'}</SelectItem>
                    <SelectItem value="normal">{language === 'zh' ? '正常' : 'Normal'}</SelectItem>
                    <SelectItem value="low">{language === 'zh' ? '微利潤' : 'Low Profit'}</SelectItem>
                    <SelectItem value="negative">{language === 'zh' ? '負利潤' : 'Negative Profit'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '倉庫' : 'Warehouse'}
                </label>
                <Select
                  value={warehouseFilter || 'all'}
                  onValueChange={(v) => setWarehouseFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-10 bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '全部倉庫' : 'All Warehouse'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部倉庫' : 'All Warehouse'}</SelectItem>
                    {warehouseOptions.map((w) => (
                      <SelectItem key={w} value={w}>{w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '產品狀態' : 'Product Status'}
                </label>
                <Select
                  value={onlineStatusFilter}
                  onValueChange={(v) => setOnlineStatusFilter(v as 'all' | 'ONLINE' | 'OFFLINE')}
                >
                  <SelectTrigger className="h-10 bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '在線狀態' : 'Online Status'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '所有狀態' : 'All Status'}</SelectItem>
                    <SelectItem value="ONLINE">{language === 'zh' ? '在線' : 'Online'}</SelectItem>
                    <SelectItem value="OFFLINE">{language === 'zh' ? '離線' : 'Offline'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '產品內容' : 'Product Description'}
                </label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品內容' : 'Search Product Description'}
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'zh' ? '品牌' : 'Brand'}
                </label>
                <Select
                  value={brandFilter || 'all'}
                  onValueChange={(v) => setBrandFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-10 bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '全部品牌' : 'All Brands'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部品牌' : 'All Brands'}</SelectItem>
                    {brandOptions.map((b) => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '重設' : 'Reset'}
                </Button>
                <Button onClick={handleSearch} className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Search className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '搜尋' : 'Search'}
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={exportToExcel}
                disabled={rows.length === 0}
                className="bg-card/50 border-border"
              >
                <Download className="w-4 h-4 mr-2" />
                {language === 'zh' ? '匯出 Excel' : 'Export Excel'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mb-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {language === 'zh' ? '顯示 ' : 'Showing '}
            {total === 0 ? 0 : (page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} / {total}
            {language === 'zh' ? ' 筆' : ' rows'}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground">{language === 'zh' ? '每頁' : 'Per page'}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
                updateUrl({ pageSize: Number(v), page: 1 });
              }}
            >
              <SelectTrigger className="h-8 w-24 bg-card/50 border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-8 bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                disabled={page <= 1 || loading}
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  updateUrl({ page: Math.max(1, page - 1) });
                }}
              >
                {language === 'zh' ? '上一頁' : 'Prev'}
              </Button>
              <span className="text-muted-foreground">
                {language === 'zh' ? '第 ' : 'Page '}{page}{language === 'zh' ? ' 頁' : ''}
              </span>
              <Button
                variant="outline"
                className="h-8 bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                disabled={page * pageSize >= total || loading}
                onClick={() => {
                  setPage((p) => p + 1);
                  updateUrl({ page: page + 1 });
                }}
              >
                {language === 'zh' ? '下一頁' : 'Next'}
              </Button>
            </div>
          </div>
        </div>

        <Card className="bg-card/80 border-border shadow-lg shadow-black/30">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400" />
                <p className="text-muted-foreground ml-3">{language === 'zh' ? '載入中...' : 'Loading...'}</p>
              </div>
            ) : errorMessage ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{errorMessage}</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {language === 'zh' ? '請設定搜尋條件後按「搜尋」，或尚無符合條件的產品' : 'Set filters and click Search, or no products match.'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/60">
                    <TableRow>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? 'SKU ID' : 'SKU ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '狀態' : 'Status'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '原價' : 'Original Price'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '特價' : 'Selling Price'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '折扣售價' : 'Discounted Price'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '佣金' : 'Commission'}</TableHead>
                      <TableHead 
                        className="text-muted-foreground cursor-pointer hover:bg-muted/40 select-none"
                        onClick={handleSortProfit}
                      >
                        <div className="flex items-center gap-1">
                          <span>{language === 'zh' ? '利潤' : 'Profit'}</span>
                          {sortConfig.key === 'finalProfit' && sortConfig.direction === 'asc' && (
                            <ChevronUp className="w-4 h-4" />
                          )}
                          {sortConfig.key === 'finalProfit' && sortConfig.direction === 'desc' && (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRows.map((r) => (
                      <TableRow key={r.id} className="hover:bg-accent/30">
                        <TableCell className="text-blue-400 font-medium">{r.sku_id}</TableCell>
                        <TableCell className="text-muted-foreground">{r.brand ?? '-'}</TableCell>
                        <TableCell className="text-muted-foreground max-w-xs">
                          <div className="line-clamp-2">
                            {decodeHtmlEntities(
                              language === 'zh'
                                ? (r.sku_name_chi || r.sku_name) || ''
                                : (r.sku_name || r.sku_name_chi) || ''
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{getStatusText(r.sku_status)}</TableCell>
                        <TableCell className="text-muted-foreground">{r.original_price ?? '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{r.selling_price ?? r.original_price ?? '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{r.effectivePrice.toFixed(2)}</TableCell>
                        <TableCell className="text-muted-foreground">{r.commissionFee.toFixed(2)}</TableCell>
                        <TableCell className={profitCellClass(r.finalProfit)}>{r.finalProfit.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PriceAnalysis;
